import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ClipboardList, Users, Inbox, Receipt, TrendingUp } from "lucide-react";

export default function AdminOverview() {
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/admin/analytics"), api.get("/work-orders")]).then(([a, w]) => {
      setAnalytics(a.data);
      setRecent(w.data.slice(0, 6));
    });
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Admin console</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">Operations overview</h1>
      </div>

      {!analytics ? (
        <div className="text-slate-400">Loading analytics…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            <Stat icon={ClipboardList} label="Active WOs" value={analytics.active_work_orders} testId="admin-active-wo" />
            <Stat icon={TrendingUp} label="Completed" value={analytics.completed_work_orders} testId="admin-completed-wo" />
            <Stat icon={Users} label="Clients" value={analytics.total_clients} testId="admin-clients" />
            <Stat icon={Receipt} label="Outstanding" value={`$${analytics.outstanding_amount.toLocaleString()}`} testId="admin-outstanding" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <SecondaryStat label="New quotes" value={analytics.new_quotes} link="/admin/quotes" />
            <SecondaryStat label="Unread contacts" value={analytics.unread_contacts} link="/admin/contacts" />
            <SecondaryStat label="Paid invoices" value={`${analytics.paid_invoices} / ${analytics.total_invoices}`} link="/admin/invoices" />
          </div>
        </>
      )}

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg tracking-tight">Recent work orders</div>
          <Link to="/admin/work-orders" className="text-xs font-semibold text-[#1E40AF]">Manage →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">ID</th>
              <th className="text-left px-5 py-3 font-semibold">Client</th>
              <th className="text-left px-5 py-3 font-semibold">Property</th>
              <th className="text-left px-5 py-3 font-semibold">Service</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-10 text-center text-slate-400">No work orders yet.</td></tr>
            )}
            {recent.map((wo) => (
              <tr key={wo.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-alt text-xs text-slate-500">{wo.id.slice(-6).toUpperCase()}</td>
                <td className="px-5 py-3 text-slate-700">{wo.client_company || wo.client_name}</td>
                <td className="px-5 py-3">
                  <Link to={`/admin/work-orders/${wo.id}`} className="text-slate-900 hover:text-[#1E40AF] font-medium">
                    {wo.property_address}
                  </Link>
                </td>
                <td className="px-5 py-3 text-slate-600">{wo.service_type}</td>
                <td className="px-5 py-3"><StatusBadge status={wo.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="font-display text-3xl tracking-tighter tabular-nums mt-2 text-[#0B192C]">{value}</div>
    </div>
  );
}

function SecondaryStat({ label, value, link }) {
  return (
    <Link to={link} className="block bg-white border border-slate-200 p-5 hover:border-[#1E40AF]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display text-2xl tracking-tighter tabular-nums mt-2 text-[#0B192C]">{value}</div>
    </Link>
  );
}
